import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { ProcedureStatus } from '@/types/cgt'

/**
 * EPIC-sourced demographics stored on the Patient record. Every field is nullable:
 * a patient can be created MRN-only (manually, or by the integration when a procedure is
 * opened) and enriched later, so the UI must render absent fields as empty state.
 */
export interface PatientDemographics {
  firstName: string | null
  lastName: string | null
  fullName: string | null
  sex: string | null
  /** ISO date string. `age` is derived server-side so it can never go stale. */
  birthDate: string | null
  email: string | null
  phone: string | null
  addressLine: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  country: string | null
  preferredLanguage: string | null
  // From the Epic ADT feed (PID segment). Optional: records created before the
  // feed carried them, and older backends, omit them.
  middleName?: string | null
  race?: string | null
  ethnicity?: string | null
  maritalStatus?: string | null
  businessPhone?: string | null
  accountNumber?: string | null
  assigningAuthority?: string | null
  identifierType?: string | null
}

/** A patient's live procedure, as returned nested on a patient read. */
export interface PatientProcedure {
  id: string
  status: ProcedureStatus
  startedAt: string
  createdBy: string
  /** When the run is planned for, if it was given one. Absent from an older backend. */
  scheduledDate?: string | null
  /** `type` is the template's kind (Transplant, MES, …); absent from an older backend. */
  procedureTemplate: { id: string; name: string; type?: string }
}

/** A value read from one of the patient's forms, and where it was read (z8nrz7g57g). */
export interface RecordedValue {
  value: string
  submissionId: string
  procedureId: string | null
  formName: string
  recordedAt: string
}

/** Clinical facts, by the Field Library system name the server reads them by. */
export type ClinicalKey =
  | 'diagnosis'
  | 'icd_code'
  | 'transplant_type'
  | 'planned_donor_type'
  | 'transplant_physician'
  | 'preparative_regimen'
  | 'protocol'
  | 'recipient_abo_rh'
  | 'recipient_weight_kg'
  | 'last_weight_kg'
  | 'cmv_status'
  | 'planned_infusion_date'
  | 'hla_report_attached'

export type DonorKey =
  | 'donor_type'
  | 'relationship'
  | 'donor_first_name'
  | 'donor_last_name'
  | 'donor_gender'
  | 'donor_date_of_birth'
  | 'donor_abo_rh'
  | 'donor_antibody_screen'
  | 'donor_eligibility'
  | 'donor_mrn_nmdp_id'
  | 'nmdp_donor_id'
  | 'donor_cbu_id'
  | 'abo_incompatibility'
  | 'date_of_idm_results'

/** One donor, as the patient's forms describe them. There is no Donor record. */
export interface RecordedDonor {
  key: string
  values: Partial<Record<DonorKey, string>>
  procedureIds: string[]
  formName: string
  recordedAt: string
}

export interface PatientHistoryEvent {
  /** ISO timestamp, or YYYY-MM-DD when `dateOnly`. */
  at: string
  dateOnly: boolean
  state: 'planned' | 'in_progress' | 'done'
  title: string
  detail: string | null
  procedureId?: string | null
  productId?: string | null
  din?: string | null
}

/** `GET /patients/:mrn/record` — what the patient page reads beyond demographics. */
export interface PatientRecord {
  clinical: Partial<Record<ClinicalKey, RecordedValue>>
  donors: RecordedDonor[]
  history: PatientHistoryEvent[]
  /** When each of the patient's products was last placed in storage, by product id. */
  storedAt: Record<string, string>
  /** Each stored product's unit and the condition it is held at, by product id. Absent from an older backend. */
  storage?: Record<string, StoragePlace>
}

/** A stored product's unit and held condition. */
export interface StoragePlace {
  unit: { code: string; name: string | null; equipmentLabel: string | null; equipmentType: string | null } | null
  condition: { code: string; label: string; temperature: string | null } | null
}

/** `progress` is null when the procedure journey could not be resolved. */
export interface PatientProcedureWithProgress extends PatientProcedure {
  progress: { completed: number; total: number } | null
  /** A form of this run is waiting for its reviews (z8nrz7dt41). Absent from an older backend. */
  hasPendingReview?: boolean
}

export interface PatientListItem extends PatientDemographics {
  id: string
  mrn: string
  createdAt: string
  age: number | null
  /** Procedures carry step progress here too — scored server-side for the whole page. */
  procedures: PatientProcedureWithProgress[]
}

export interface PatientDetail extends PatientDemographics {
  id: string
  mrn: string
  createdBy: string
  createdAt: string
  updatedAt: string
  age: number | null
  procedures: PatientProcedureWithProgress[]
}

/**
 * A demographics correction (z8nrz7dp78): only the fields that change, `null` to
 * clear one, plus the reason and the single-use signing token. Never the signer
 * and never the MRN — the server refuses both.
 */
export type UpdatePatientInput = Partial<
  Record<
    | 'firstName'
    | 'lastName'
    | 'sex'
    | 'birthDate'
    | 'preferredLanguage'
    | 'email'
    | 'phone'
    | 'addressLine'
    | 'city'
    | 'state'
    | 'postalCode'
    | 'country',
    string | null
  >
> & { reason: string; signingToken: string }

/** Only `mrn` is required; every demographic field is optional. */
export interface CreatePatientInput extends Partial<Omit<PatientDemographics, 'birthDate'>> {
  mrn: string
  birthDate?: string
}

/**
 * A provider as HL7 carried it (PV1-7 attending / PV1-17 admitting). Every
 * component is optional because the emitter omits what the message did not send;
 * format with `encounterProviderName` rather than assuming a shape.
 */
export interface EncounterProvider {
  id?: string
  lastName?: string
  firstName?: string
  middleName?: string
  idType?: string
}

/**
 * One Epic visit, from an ADT's PV1 context (`patient.encounter.upsert`).
 *
 * This is hospital visit history, NOT a Title21 procedure — the Patient History
 * dialog shows them on separate tabs for exactly that reason. Values are as Epic
 * sent them: `patientClass` may be `I`, `OUT` or `INPATIENT` depending on the
 * sender, so it is displayed, never switched on.
 *
 * `admittedAt` is an instant derived from a ZONE-LESS HL7 wall clock, so it must
 * be rendered in UTC — `admittedAtPrecision` says how much of it is real
 * (a `day` precision admit means midnight is padding, not a time).
 */
export interface PatientEncounter {
  id: string
  mrn: string
  mrnAssigningAuthority: string | null
  encounterNumber: string
  identifierSystem: string
  accountNumber: string | null
  patientClass: string | null
  patientType: string | null
  pointOfCare: string | null
  room: string | null
  bed: string | null
  facility: string | null
  attendingProvider: EncounterProvider | null
  admittingProvider: EncounterProvider | null
  admittedAt: string | null
  admittedAtHl7: string | null
  admittedAtPrecision: string | null
  dischargedAt: string | null
  dischargedAtHl7: string | null
  dischargedAtPrecision: string | null
  status: 'active' | 'discharged'
  lastTrigger: string | null
  lastEventAt: string | null
  sourceSystem: string | null
  sourceMessageId: string | null
  createdAt: string
  updatedAt: string
}

export interface SyncResult {
  proceduresLinked: number
  patientsCreated: number
}

export interface PatientPage {
  items: PatientListItem[]
  total: number
}

/**
 * Nest error bodies are `{ statusCode, message, error }`, where `message` is either a
 * string or an array of validation failures. Surfacing that beats throwing the raw
 * JSON at the user — a duplicate MRN should read as its 409 message, not as
 * `{"statusCode":409,...}`.
 */
function errorMessage(body: string, fallback: string): string {
  try {
    const parsed: unknown = JSON.parse(body)
    if (parsed && typeof parsed === 'object' && 'message' in parsed) {
      const m = (parsed as { message: unknown }).message
      if (typeof m === 'string') return m
      if (Array.isArray(m) && m.length > 0) return m.join(', ')
    }
  } catch {
    // Not JSON — fall through to the raw text.
  }
  return body || fallback
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    // `...init` must come FIRST: spreading it after `headers` lets a caller-supplied
    // `headers` replace the computed ones wholesale, dropping the Authorization
    // bearer and turning every such call into a 401 → redirect to /login.
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(errorMessage(text, `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`))
  }
  return res.json() as Promise<T>
}

function buildQs(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export const patientsApi = {
  /**
   * Patients with demographics and their procedures, preloaded in one response —
   * including each procedure's status and step progress, scored server-side for
   * the whole page. Never re-derive progress per row on the client: that fan-out
   * is exactly what this endpoint replaced.
   */
  list: (params: { q?: string; procedureTemplateId?: string; skip?: number; take?: number; sort?: string } = {}): Promise<PatientPage> =>
    // `sort` is the column sort, applied server-side before paging (z8nrz7e4h6).
    request<PatientPage>(`/patients${buildQs({ isTestData: 'false', q: params.q, procedureTemplateId: params.procedureTemplateId, skip: params.skip, take: params.take, sort: params.sort })}`),

  /** One patient: demographics plus every procedure with its step progress. */
  get: (mrn: string): Promise<PatientDetail> =>
    request<PatientDetail>(`/patients/${encodeURIComponent(mrn)}`),

  /** Register a patient. Only `mrn` is required; 409 when the MRN is taken. */
  create: (input: CreatePatientInput): Promise<PatientDetail> =>
    // No explicit Content-Type: authHeaders already sets it.
    request<PatientDetail>('/patients', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Correct a patient's demographics (z8nrz7dp78). Signed: 400 when the token is
   * spent or the password was wrong, 403 without `patients.edit`. Returns the
   * patient as `get` reads it, age re-derived.
   */
  update: (mrn: string, input: UpdatePatientInput): Promise<PatientDetail> =>
    request<PatientDetail>(`/patients/${encodeURIComponent(mrn)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  /**
   * A patient's Epic visit history, newest first. 404s an MRN this tenant cannot
   * see — deliberately not an empty array, which would read as "no visits".
   */
  encounters: (mrn: string): Promise<PatientEncounter[]> =>
    request<PatientEncounter[]>(`/patients/${encodeURIComponent(mrn)}/encounters`),

  /** Clinical facts, donors and history, read from the patient's forms, products and results. */
  record: (mrn: string): Promise<PatientRecord> =>
    request<PatientRecord>(`/patients/${encodeURIComponent(mrn)}/record`),

  /** One-time: link procedures that have no patient to a Patient by MRN. */
  sync: (): Promise<SyncResult> => request<SyncResult>('/patients/sync', { method: 'POST' }),
}
